import type { ButtonHTMLAttributes, CSSProperties } from "react";
import { BUTTON_RESET, pressClass } from "./press";
import { scaledFont } from "@/styles/scaledFont";

export interface PillProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  /** The action's copy — always `t('…')`, never a literal. */
  label: string;
  onClick: () => void;
  /** `"submit"` when the pill is a real form's action — the You stack's
   * "Save & recalculate" is one, and a `type="button"` there would break
   * Enter-to-submit. Mirrors `FillPill`. @default "button" */
  type?: "button" | "submit";
}

/* 05-SHARED.md: the main action is **not** a container. Centred text plus an
 * arrow, and nothing else: no background, no border, no radius, no height. */
const BASE: CSSProperties = {
  ...BUTTON_RESET,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--space-2)",
  padding: "10px 2px",
  width: "100%",
  color: "var(--d-fg-2)",
  font: "var(--t-ui)",
};

const ARROW: CSSProperties = {
  color: "var(--d-fg-3)",
  font: scaledFont("400 16px/20px var(--font-body)"),
};

/**
 * The main action of a screen — text and an arrow, never a filled slab. The
 * opaque one is `FillPill`, and a screen has at most one of those.
 *
 * Ported from `Pill` in
 * `docs/design/handoff/design/templates/friends/friends-screen.jsx`.
 */
export function Pill({
  label,
  onClick,
  type = "button",
  className,
  style,
  ...rest
}: PillProps): JSX.Element {
  return (
    <button
      type={type}
      className={pressClass(className)}
      onClick={onClick}
      style={{ ...BASE, ...style }}
      {...rest}
    >
      <span>{label}</span>
      <span data-pill-arrow aria-hidden="true" style={ARROW}>
        →
      </span>
    </button>
  );
}

export default Pill;
